import {useState, useEffect} from 'react';
import Button from '../../../components/button/button';

import PageContainer, { SettingsSection, SettingsActions } from "../components/settings-layout";

import {classNames} from "../../../helpers/utils/css-utils";

import SelectInput from "../../../components/forms/select";
import TextBox from "../../../components/forms/text-box";

const ttsModelsByProvider = {
    deepgram: [
        {
            name: "aura-2-thalia-en",
            value: "aura-2-thalia-en"
        },
    ],
    elevenlabs: [
        {
            name: "eleven_v3",
            value: "eleven_v3"
        },
        {
            name: "eleven_flash_v2_5",
            value: "eleven_flash_v2_5"
        },
        {
            name: "eleven_multilingual_v2",
            value: "eleven_multilingual_v2"
        }
    ],
    openai: [
        {
            name: "tts-1",
            value: "tts-1"
        },
        {
            name: "tts-1-hd",
            value: "tts-1-hd"
        }
    ]
};

const sttModelsByProvider = {
    deepgram: [
        {
            name: "nova-3",
            value: "nova-3"
        },
        {
            name: "nova-2",
            value: "nova-2"
        },
        {
             name: "nova",
             value: "nova"
        }
    ],
    assemblyai: [
        {
            name: "universal-streaming-english",
            value: "universal-streaming-english"
        },
        {
            name: "universal-streaming-multi",
            value: "universal-streaming-multi"
        }
    ],
    elevenlabs: [
        {
            name: "scribe_v1_realtime",
            value: "scribe_v1_realtime"
        },
        {
            name: "scribe_v1",
            value: "scribe_v1"
        }
    ],
};

function Badge({text, indicate}) {
    const svgClass = classNames(indicate? "fill-green-500": "fill-gray-400", "h-1.5 w-1.5")
    return (
        <span
            className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
            <svg className={svgClass} viewBox="0 0 6 6" aria-hidden="true">
                <circle cx={3} cy={3} r={3}/>
            </svg>
            {text}
        </span>
    )
}

export default function VoiceAISettings() {
    
    // Text To Speech State
    const [ttsFormData, setTtsFormData] = useState({
        provider: 'deepgram',
        model: 'aura-2-thalia-en',
        apiKey: ''
    });
    const [ttsModels, setTtsModels] = useState([]);
    const [ttsError, setTtsError] = useState({ apiKey: false });
    const [ttsValidateStatus] = useState({ status: false });

    // Speech To Text State
    const [sttFormData, setSttFormData] = useState({
        provider: 'deepgram',
        model: 'nova-3',
        apiKey: ''
    });
    const [sttModels, setSttModels] = useState([]);
    const [sttError, setSttError] = useState({ apiKey: false });
    const [sttValidateStatus] = useState({ status: false });

    // Handle TTS Changes
    const handleTtsChange = event => {
        const targetName = event.target.name;
        const targetValue = event.target.value;
        const newFormData = {...ttsFormData};
        newFormData[targetName] = targetValue;
        setTtsError({ [targetName]: false });
        setTtsFormData(newFormData);
    };

    // Handle STT Changes
    const handleSttChange = event => {
        const targetName = event.target.name;
        const targetValue = event.target.value;
        const newFormData = {...sttFormData};
        newFormData[targetName] = targetValue;
        setSttError({ [targetName]: false });
        setSttFormData(newFormData);
    };

    const handleOnSubmit = event => event.preventDefault();

    // Update TTS Models
    useEffect( () => {
        const providerModels = ttsModelsByProvider[ttsFormData.provider] || [];
        setTtsModels(providerModels);
        if (providerModels.length > 0 && (!ttsFormData.model || !providerModels.find(m => m.value === ttsFormData.model))) {
             setTtsFormData(prev => ({...prev, model: providerModels[0].value}));
        }
    }, [ttsFormData.provider, ttsFormData.model]);

    // Update STT Models
    useEffect( () => {
        const providerModels = sttModelsByProvider[sttFormData.provider] || [];
        setSttModels(providerModels);
        if (providerModels.length > 0 && (!sttFormData.model || !providerModels.find(m => m.value === sttFormData.model))) {
             setSttFormData(prev => ({...prev, model: providerModels[0].value}));
        }
    }, [sttFormData.provider, sttFormData.model]);

    return (
        <PageContainer
            navigationPosition="below-header"
            title="Voice AI Settings"
            subtitle="Configure how your agent listens and speaks.">

            <form className="flex flex-col gap-6" onSubmit={handleOnSubmit}>

                <div role="note" className="rounded-xl border border-gray-200 bg-white p-4 text-sm leading-6 text-gray-600">Voice settings are not connected to saving or key validation yet. Changes here are temporary.</div>
                <div className="grid items-start gap-6 xl:grid-cols-2">
                {/* Speech To Text Section */}
                <SettingsSection title="Speech to text" description="Turn spoken input into text your agent can understand.">
                    
                    <SelectInput
                        rootClass="w-full"
                        label="Provider*"
                        id="stt_provider"
                        name="provider"
                        value={sttFormData.provider}
                        onChange={handleSttChange}
                        optionsComp={<>
                            <option value="deepgram">Deepgram</option>
                            <option value="assemblyai">AssemblyAI</option>
                            <option value="elevenlabs">Elevenlabs</option>
                        </>}
                    />

                    <SelectInput
                        rootClass="w-full"
                        label="Model*"
                        id="stt_model"
                        name="model"
                        value={sttFormData.model}
                        onChange={handleSttChange}
                        optionsComp={<>
                            {sttModels.map(model => (
                                <option key={model.value} value={model.value}>{model.name}</option>
                            ))}
                        </>}
                    />

                    <TextBox
                        rootClass="w-full"
                        label="API Key"
                        type="password"
                        name="apiKey"
                        id="stt_apiKey"
                        value={sttFormData.apiKey}
                        placeholder="Enter API Key"
                        onChange={handleSttChange}
                        error={sttError.apiKey}
                    />

                    <TextBox
                        rootClass="w-full"
                        label="End Of Turn Timeout (ms)"
                        type="number"
                        name="eotTimeout"
                        id="eotTimeout"
                        value={sttFormData.eotTimeout}
                        placeholder="200"
                        min="100"
                        max="5000"
                        onChange={handleSttChange}
                        error={sttError.eotTimeout}
                    />

                    <div className="bg-transparent flex flex-row items-center gap-4">
                        <Button color="default" variant="button" type="button" disabled text="Validate Key" />
                        <Badge text={sttValidateStatus.status? "Valid Key": "Unverified"} indicate={sttValidateStatus.status}/>
                    </div>
                </SettingsSection>

                {/* Text To Speech Section */}
                <SettingsSection title="Text to speech" description="Give your agent a voice for spoken responses.">
                    
                    <SelectInput
                        rootClass="w-full"
                        label="Provider*"
                        id="tts_provider"
                        name="provider"
                        value={ttsFormData.provider}
                        onChange={handleTtsChange}
                        optionsComp={<>
                            <option value="deepgram">Deepgram</option>
                            <option value="openai">OpenAI</option>
                            <option value="elevenlabs">ElevenLabs</option>
                        </>}
                    />

                    <SelectInput
                        rootClass="w-full"
                        label="Model*"
                        id="tts_model"
                        name="model"
                        value={ttsFormData.model}
                        onChange={handleTtsChange}
                        optionsComp={<>
                            {ttsModels.map(model => (
                                <option key={model.value} value={model.value}>{model.name}</option>
                            ))}
                        </>}
                    />

                    <TextBox
                        rootClass="w-full"
                        label="API Key"
                        type="password"
                        name="apiKey"
                        id="tts_apiKey"
                        value={ttsFormData.apiKey}
                        placeholder="Enter API Key"
                        onChange={handleTtsChange}
                        error={ttsError.apiKey}
                    />

                    <div className="bg-transparent flex flex-row items-center gap-4">
                        <Button color="default" variant="button" type="button" disabled text="Validate Key" />
                        <Badge text={ttsValidateStatus.status? "Valid Key": "Unverified"} indicate={ttsValidateStatus.status}/>
                    </div>
                </SettingsSection>
                </div>

                <SettingsActions>
                    <Button variant="button" type="submit" disabled text="Save voice settings" />
                </SettingsActions>
            </form>

        </PageContainer>
    )
}
